import moment, { Moment } from "moment";
import tzMoment from "moment-timezone";
import { Location } from "../queries/auto-generated/schemas";
import { getTodaysHours, TIME_INPUT_FORMAT } from "../helpers";
import _ from "lodash";
import {
  HUMAN_OUTPUT_FORMAT,
  MAX_TIME_SLOTS,
  SQUARE_TIME_INPUT_FORMAT,
} from "../constants";

/**
 *
 * Calculate first available time slot
 * @returns Moment
 */
export const calcFirstAvailableTime = (
  currentTime: Moment,
  leadTime: number,
  timeSlotIncrement: number
) => {
  const withLeadTime = moment(currentTime).add(leadTime, "minutes");
  const timeBump =
    (timeSlotIncrement - (withLeadTime.minutes() % timeSlotIncrement)) %
    timeSlotIncrement;
  const first = moment(withLeadTime).add(timeBump, "minutes");
  return removeSecondsAndMilliSeconds(first);
};

/**
 * Calculate available time slots given a time and location
 * @param currentTime Moment
 * @param location Location
 * @returns Moment[]
 */
export const calcTimeSlots = (
  currentTime: Moment,
  location: Location
): Moment[] => {
  const todaysHours = getTodaysHours(location);
  if (todaysHours === null) {
    return [];
  }

  const {
    lead_time: unchecked_lead_time,
    time_slot_increment: unchecked_time_slot_increment,
  } = location;

  // Make sure that the lead time is non-negative
  const lead_time = Math.max(0, unchecked_lead_time ?? 0);
  // Make sure that the time increment is positive otherwise the while loop may loop indefinitely
  const time_slot_increment = Math.max(1, unchecked_time_slot_increment ?? 0);

  const { periods } = todaysHours;

  // Generate time slots per current opening hours
  return _.flatten(
    periods.map((period) => {
      const timeSlots: Moment[] = [];
      const { start_local_time, end_local_time } = period;

      const opening = moment(start_local_time, TIME_INPUT_FORMAT);
      const closing = moment(end_local_time, TIME_INPUT_FORMAT);

      // Do not display time slots before opening
      if (currentTime.isBefore(opening)) {
        return [];
      }

      // Get first time slot
      const first = calcFirstAvailableTime(
        currentTime,
        lead_time,
        time_slot_increment
      );

      // Check that the first slot is not after the last call
      if (first.isSameOrAfter(closing)) {
        return [];
      }

      timeSlots.push(first);

      // Create remaining time slots
      let shouldBreak = false;
      let previous = first;
      while (!shouldBreak && timeSlots.length < MAX_TIME_SLOTS) {
        const next = moment(previous).add(time_slot_increment, "minutes");
        previous = next;
        if (next.isBefore(closing)) {
          timeSlots.push(next);
        } else {
          shouldBreak = true;
        }
      }

      return timeSlots;
    })
  );
};

export const adjustForTimeZone = (
  inputString: string,
  inputFormat: string,
  timezone: string
) => {
  return tzMoment.tz(inputString, inputFormat, timezone);
};

export const makeRelativeTimeAbsolute = (
  localTime: Moment,
  dateString: string
): Moment =>
  moment(dateString + " " + localTime.format(SQUARE_TIME_INPUT_FORMAT));

export const removeSecondsAndMilliSeconds = (m: Moment) => {
  return m.seconds(0).millisecond(0);
};

export const createHumanReadable = (
  RFC3339TimeStamp: string,
  timezone: string
) => {
  return tzMoment.utc(RFC3339TimeStamp).tz(timezone).format(HUMAN_OUTPUT_FORMAT);
};
